import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/UseAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faCompass,
  faChartLine,
  faUser,
  faMugHot,
  faImages,
  faFileAlt,
  faSignOutAlt,
  faShoppingBag,
  faCog,
  faChalkboardTeacher, // <-- 1. Importar ícono de Cursos
} from '@fortawesome/free-solid-svg-icons';
import { useLanguageLink } from '../../hooks/useLanguageLink';

// ID de la categoría "Cursos"
const COURSE_CREATOR_CATEGORY_ID = '68b095babef356dc6d3dd52d';

const navItems = [
  { labelKey: 'sidebar.home', icon: faHome, to: '/', show: () => true },
  {
    labelKey: 'sidebar.explore',
    icon: faCompass,
    to: '/explore',
    show: () => true,
  },
  {
    labelKey: 'sidebar.dashboard',
    icon: faChartLine,
    to: '/dashboard',
    show: (user) => user?.profileType === 'creator',
  },
  {
    labelKey: 'sidebar.myProfile',
    icon: faUser,
    to: '/my/profile',
    show: (user) => !!user,
  },
  {
    labelKey: 'sidebar.settings',
    icon: faCog,
    to: '/my/settings',
    show: (user) => !!user,
  },
  // --- 2. Lógica de "Switch" para Creadores ---
  {
    labelKey: 'sidebar.myPosts',
    icon: faFileAlt,
    to: '/my/posts',
    // Mostrar solo si es Creador Y NO es Creador de Cursos
    show: (user) =>
      user?.profileType === 'creator' &&
      user?.category?._id.toString() !== COURSE_CREATOR_CATEGORY_ID,
  },
  {
    labelKey: 'sidebar.myPacks',
    icon: faImages,
    to: '/my/packs',
    // Mostrar solo si es Creador Y NO es Creador de Cursos
    show: (user) =>
      user?.profileType === 'creator' &&
      user?.category?._id.toString() !== COURSE_CREATOR_CATEGORY_ID,
  },
  {
    labelKey: 'navbar.myCourses', // Reutilizamos la clave de i18n de Navbar
    icon: faChalkboardTeacher,
    to: '/my/courses',
    // Mostrar solo si es Creador Y SÍ es Creador de Cursos
    show: (user) =>
      user?.profileType === 'creator' &&
      user?.category?._id.toString() === COURSE_CREATOR_CATEGORY_ID,
  },
  // --- Fin de la lógica de "Switch" ---
  {
    labelKey: 'sidebar.myDonations',
    icon: faMugHot,
    to: '/my/donations',
    show: (user) => !!user,
  },
  {
    labelKey: 'sidebar.myPurchases',
    icon: faShoppingBag,
    to: '/my/purchases',
    show: (user) => !!user,
  },
];

function Sidebar({ onLinkClick }) {
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const { langLink } = useLanguageLink();

  const currentPathWithoutLang = location.pathname.startsWith(
    `/${i18n.language}`
  )
    ? location.pathname.substring(i18n.language.length + 1)
    : location.pathname;

  const isActive = (to) => {
    if (to === '/') {
      return currentPathWithoutLang === '/' || currentPathWithoutLang === '';
    }
    return currentPathWithoutLang.startsWith(to);
  };

  const handleLogout = () => {
    if (onLinkClick) onLinkClick();
    logout();
  };

  return (
    <aside className="h-full flex flex-col p-4 bg-white dark:bg-neutral-800 w-64 lg:border-r lg:border-neutral-200 lg:dark:border-neutral-700 overflow-y-auto">
      <nav className="flex-1 flex flex-col gap-1">
        {navItems
          .filter((item) => item.show(user)) // El filtro ahora aplicará la nueva lógica
          .map((item) => {
            const to = typeof item.to === 'function' ? item.to(user) : item.to;
            return (
              <Link
                to={langLink(to)}
                key={item.labelKey}
                onClick={onLinkClick}
                className={`flex items-center gap-4 rounded-lg px-4 py-3 text-base font-semibold transition-colors ${
                  isActive(to)
                    ? 'bg-primary/10 text-primary dark:bg-primary/20'
                    : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700/60'
                }`}
              >
                <FontAwesomeIcon icon={item.icon} className="w-5 text-center" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
      </nav>
      {isAuthenticated && (
        <div className="mt-auto">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 rounded-lg px-4 py-3 text-base font-semibold text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="w-5 text-center" />
            <span>{t('sidebar.logout')}</span>
          </button>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
